import { useEffect, useState } from "react";
import { advancesApi, type OpenAdvance } from "../lib/api";
import { fmtAmount } from "../lib/money";
import { fmtDate } from "../companies/CompanyPicker";

// A receipt or payment line not set against any bill: asked before saving whether it is an advance (sql/258_a).
// An advance gets its own reference (ADV-0001 …) that a later invoice or bill is set against. Money received in advance
// for services carries GST when it is received; for goods it doesn't.
export function AdvancePrompt({ party, amount, base, gst, busy, onAdvance, onBills, onAccount, onCancel }: {
  party: string; amount: number; base: "receipt" | "payment"; gst: boolean; busy: boolean;
  onAdvance: (rate: number | null) => void; onBills: () => void; onAccount: () => void; onCancel: () => void;
}) {
  const [what, setWhat] = useState<"goods" | "services">("goods");
  const [rate, setRate] = useState("18");
  const askGst = base === "receipt" && gst;
  return (
    <div className="card panel advance-ask" role="dialog" aria-label="Is this an advance?"
      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); onCancel(); } }}>
      <p><strong>₹{fmtAmount(amount)} {base === "receipt" ? "from" : "to"} {party} isn't set against any {base === "receipt" ? "invoice" : "bill"}.</strong> Is it an advance?</p>
      {askGst && (
        <div className="fields inline">
          <label>For<select value={what} onChange={(e) => setWhat(e.target.value as "goods" | "services")} aria-label="Advance for">
            <option value="goods">Goods (no GST on the advance)</option><option value="services">Services (GST on the advance)</option></select></label>
          {what === "services" && <label>GST rate<select value={rate} onChange={(e) => setRate(e.target.value)} aria-label="GST rate on the advance">
            {["5", "12", "18", "28", "40"].map((r) => <option key={r} value={r}>{r}%</option>)}</select></label>}
          {what === "services" && <span className="muted small">GST of ₹{fmtAmount(Math.round((amount - amount * 100 / (100 + Number(rate))) * 100) / 100)} is inside the amount; it's
            paid now and taken back when the invoice is raised (GSTR-1 tables 11A and 11B).</span>}
        </div>
      )}
      <div className="row">
        <button type="button" className="primary" disabled={busy} autoFocus onClick={() => onAdvance(askGst && what === "services" ? Number(rate) : null)}>Yes, an advance</button>
        <button type="button" disabled={busy} onClick={onBills}>Set it against open {base === "receipt" ? "invoices" : "bills"}</button>
        <button type="button" disabled={busy} onClick={onAccount}>Leave on account</button>
        <button type="button" className="link" onClick={onCancel}>Back <kbd>Esc</kbd></button>
      </div>
    </div>
  );
}

// On an invoice or bill: the party's open advances, to set against it
export function AdvanceAdjust({ company, party, date, base, total, value, onChange }: {
  company: string; party: string | null; date: string; base: "sales" | "purchase"; total: number;
  value: { ref_name: string; amount: number }[] | undefined; onChange: (v: { ref_name: string; amount: number }[] | undefined) => void;
}) {
  const [adv, setAdv] = useState<OpenAdvance[]>([]);
  useEffect(() => {
    setAdv([]);
    if (party && date) advancesApi.open(company, party, date).then(setAdv).catch(() => setAdv([]));
  }, [company, party, date]);
  if (!adv.length) return null;
  const amt = (ref: string) => value?.find((a) => a.ref_name === ref)?.amount;
  const set = (ref: string, n: number | undefined) => {
    const rest = (value ?? []).filter((a) => a.ref_name !== ref);
    onChange(n ? [...rest, { ref_name: ref, amount: n }] : rest.length ? rest : undefined);
  };
  const used = (value ?? []).reduce((t, a) => t + a.amount, 0);
  const over = total > 0 && used > total + 0.005;
  return (
    <fieldset className={`section-box ${over ? "bad" : ""}`} id="advances">
      <legend>Advances {base === "sales" ? "received from this customer" : "paid to this vendor"}</legend>
      <table className="grid-table compact">
        <thead><tr><th>Advance</th><th>Date</th><th className="num">Open</th><th className="num">Set against this {base === "sales" ? "invoice" : "bill"}</th></tr></thead>
        <tbody>{adv.map((a) => (
          <tr key={a.ref_name}>
            <td>{a.ref_name}{a.gst && <span className="muted small"> · GST {a.gst.rate}% paid on it</span>}</td>
            <td>{a.date ? fmtDate(a.date) : ""}</td><td className="num">{fmtAmount(a.open)}</td>
            <td className="num"><label className="check"><input type="checkbox" checked={amt(a.ref_name) != null}
              onChange={(e) => set(a.ref_name, e.target.checked ? Math.min(a.open, Math.max(total - used, 0) || a.open) : undefined)} aria-label={`Use ${a.ref_name}`} />
              {amt(a.ref_name) != null && <input className="num short" inputMode="decimal" value={amt(a.ref_name)} aria-label={`Amount of ${a.ref_name}`}
                onChange={(e) => set(a.ref_name, Math.min(Number(e.target.value) || 0, a.open) || undefined)} />}</label></td>
          </tr>))}</tbody>
      </table>
      {over ? <p className="msg err">The advances set against it (₹{fmtAmount(used)}) are more than it comes to (₹{fmtAmount(total)}): lower one of them.</p>
        : used > 0 && <p className="muted small">₹{fmtAmount(used)} of advances settle it{total > used ? `; ₹${fmtAmount(Math.round((total - used) * 100) / 100)} stays due` : ""}.</p>}
    </fieldset>
  );
}

// GSTR-1 tables 11A (advances received, tax paid) and 11B (advances set against invoices this period, tax taken back)
export function GstAdvances({ company, registration, period }: { company: string; registration: string; period: string }) {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof advancesApi.gst>> | null>(null);
  useEffect(() => { advancesApi.gst(company, registration, period).then(setRows).catch(() => setRows([])); }, [company, registration, period]);
  if (!rows?.length) return null;
  return (
    <>
      <h3>Advances (tables 11A and 11B)</h3>
      <table className="grid-table compact">
        <thead><tr><th>Table</th><th>Place of supply</th><th className="num">Rate</th><th className="num">Advance (before tax)</th><th className="num">IGST</th><th className="num">CGST</th><th className="num">SGST</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}><td>{r.kind === "at" ? "11A Advance received" : "11B Adjusted against invoices"}</td><td>{r.pos}{r.intra ? " (within the state)" : ""}</td>
            <td className="num">{r.rate}%</td><td className="num">{fmtAmount(r.taxable)}</td><td className="num">{fmtAmount(r.igst)}</td><td className="num">{fmtAmount(r.cgst)}</td><td className="num">{fmtAmount(r.sgst)}</td></tr>))}</tbody>
      </table>
    </>
  );
}
